// Sharing a point of interest, and opening one that was shared.
//
// Two deterministic jobs, kept out of the map and dialog components so they can
// be tested without a Mapbox instance:
//
//   * what a POI's share link looks like, and what the clipboard gets
//   * what a `?poi_id=…&poi_class=…` deep link asks the map to do
//
// The address shapes themselves are the backend's — see `PoiUrl` — and the
// server hands the built URL down on the POI payload (`shareUrl`). Nothing here
// builds a canonical address itself, because a POI's country and region are not
// on the map tiles it was tapped from; guessing one would produce a link that
// resolves to nothing.
//
// The components keep only the side effects: the flyTo, the clipboard write and
// the once-per-page-load flag.

// Close enough to see which pin the link meant, without dropping the walker in
// with no context around it. Matches SHARED_HIGHLIGHT_MIN_ZOOM's reasoning.
export const SHARED_POI_MIN_ZOOM = 15;

export interface SharedPoiRequest {
  id: string;
  klass: string;
  /** The POI subtype, e.g. `peak` — what the map switches a layer on for. */
  type: string | null;
  lat: number | null;
  lng: number | null;
}

// Which POI (if any) a URL asks the map to open.
//
// `poi_class` is required alongside `poi_id`: amenities and accommodations are
// separate tables with overlapping primary keys, so an id on its own names two
// different places and the dialog would have to guess which to fetch.
export function sharedPoiFromUrl(currentUrl: string): SharedPoiRequest | null {
  let params: URLSearchParams;
  try {
    params = new URL(currentUrl).searchParams;
  } catch (e) {
    return null;
  }

  const id = params.get('poi_id');
  const klass = params.get('poi_class');
  if (!id || !klass) return null;

  return {
    id,
    klass,
    type: params.get('poi') || null,
    lat: finiteOrNull(params.get('lat')),
    lng: finiteOrNull(params.get('lng')),
  };
}

function finiteOrNull(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

// Where to point the camera, or null when the link carried no coordinate — in
// which case the map should stay where it is rather than fly to (0, 0).
export function sharedPoiCenter(request: SharedPoiRequest | null): [number, number] | null {
  if (!request || request.lat === null || request.lng === null) return null;
  return [request.lng, request.lat];
}

// Never zooms out: someone already closer in keeps their view.
export function sharedPoiZoom(currentZoom: any): number {
  const zoom = Number(currentZoom);
  return Number.isFinite(zoom) ? Math.max(zoom, SHARED_POI_MIN_ZOOM) : SHARED_POI_MIN_ZOOM;
}

// The POI layer selection to apply, with the shared POI's own type merged into
// whatever the walker already had on.
//
// A union, not a replacement: `poi` doubles as the map's layer-selection
// parameter, so setting it to just the shared type would silently switch off
// every other layer the walker had chosen. Returns null when the link named no
// type, meaning "change nothing".
export function sharedPoiSelection(
  request: SharedPoiRequest | null,
  currentSelection: string[] | null | undefined,
): string[] | null {
  if (!request || !request.type) return null;

  const current = Array.isArray(currentSelection) ? currentSelection.filter(Boolean) : [];
  if (current.includes(request.type)) return current;

  return [...current, request.type];
}

// --- Sharing ---------------------------------------------------------------

// The link to share for a POI.
//
// `shareUrl` is what the server hands down and is always preferred — only it
// knows the POI's country and region, which are not on the map tiles. It is
// never null for a POI the backend answered for. (`canonicalUrl` IS nullable and
// must not be used here.)
//
// When the record has not been fetched — the sheet opens from the tile seed and
// the query is still in flight, or it failed — the id address is rebuilt here
// instead. That needs only the type and the id, both of which the tile carries,
// and it is the same address the server would have returned: `/peaks/<id>` for a
// peak, `/poi/<subtype>/<id>` for everything else, each redirecting to the
// canonical address once the geo backfill reaches the row.
//
// Without this the Share action disappeared whenever the fetch missed, which is
// the one moment the walker has no other way to get a link.
export function poiShareUrl(poi: any, origin?: string): string | null {
  if (!poi) return null;

  const url = poi.shareUrl || poi.share_url;
  if (typeof url === 'string' && url.length > 0) return url;

  const path = poiFallbackPath(poi);
  if (!path) return null;

  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}${path}`;
}

// The id address, or null when the POI carries too little to build one.
//
// A peak is never addressed as `/poi/peak/...`: both forms resolve, but the
// `/peaks` one is what we hand out, so the two platforms and the server all
// produce the same link for the same place.
export function poiFallbackPath(poi: any): string | null {
  const id = poi?.id;
  const type = poi?.type;
  if (id === undefined || id === null || String(id).length === 0) return null;

  if (type === 'peak') return `/peaks/${id}`;
  if (typeof type !== 'string' || type.length === 0) return null;

  return `/poi/${type}/${id}`;
}

// What goes on the clipboard, and into a share sheet: the name and the link.
// Falls back to the link alone for an unnamed POI rather than emitting a stray
// newline.
export function poiShareText(poi: any): string | null {
  const url = poiShareUrl(poi);
  if (!url) return null;

  const name = typeof poi?.name === 'string' ? poi.name.trim() : '';
  return name ? `${name}\n${url}` : url;
}

// The Web Share API payload, for browsers that have one.
export function poiSharePayload(poi: any): { title: string; url: string } | null {
  const url = poiShareUrl(poi);
  if (!url) return null;

  const name = typeof poi?.name === 'string' ? poi.name.trim() : '';
  return { title: name || 'HiiKER', url };
}
